import { createClient } from "@connectrpc/connect";
import { ISaveService } from "../../../abstractions/services/actions/ISaveService";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { FertilizacionService } from "../../../generated/grpc/fertilizacion_pb";
import { createCachedFactory } from "../../../utils/factoryUtils";
import { grpcFertilizacionDatabase } from "../database/GrpcFertilizacionDatabase";
import { AsignarLotesRequest } from "../request/AsignarLotesRequest";
import { AnalisisLaboratorioResponse } from "../response/AnalisisLaboratorioResponse";
import { fx_createFertilizacionGrpcService, toAnalisisLaboratorioResponse } from "./FertilizacionGrpcService";

/** Variante gRPC-Web de `FertilizacionAsignarLotesService`/`FertilizacionAsignarLotesSaveService`
 *  (ver esos archivos): junta ambos roles en una sola clase -acá el
 *  `analisisId` viaja en el propio mensaje del RPC, no hace falta el adaptador
 *  que le inyectaba el id a `ISaveService` en la variante REST-. Esta llamada
 *  es la que dispara la (re)generación de la recomendación en el server (vía
 *  el microservicio externo de L3), todo-o-nada. */
export class FertilizacionAsignarLotesGrpcService
  implements ISaveService<AsignarLotesRequest, AnalisisLaboratorioResponse>
{
  constructor(private readonly _analisisId: string) {}

  async saveAsync(
    request: AsignarLotesRequest,
  ): Promise<AnalisisLaboratorioResponse> {
    const client = createClient(FertilizacionService, grpcTransportProvider.get());

    let saved;
    try {
      saved = await client.asignarLotes({
        analisisId: this._analisisId,
        asignaciones: request.asignaciones.map((asignacion) => ({
          registroId: asignacion.registroId,
          loteId: asignacion.loteId,
        })),
      });
    } catch (error) {
      throw mapConnectError(error);
    }

    await grpcFertilizacionDatabase.putOneAsync(saved);
    fx_createFertilizacionGrpcService(saved.fincaId).clearCache();
    return toAnalisisLaboratorioResponse(saved);
  }
}

/** Factory cacheada: una única instancia por analisisId (mismo criterio que
 *  `fx_createFertilizacionAsignarLotesSaveService`). */
export const fx_createFertilizacionAsignarLotesGrpcService = createCachedFactory(
  (analisisId: string) => new FertilizacionAsignarLotesGrpcService(analisisId),
);
